import { Button } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";

/**
 * @typedef {Object} DeleteModalProps
 * @property {boolean} showModal - Controls the visibility of the modal
 * @property {() => void} handleClose - Function to close the modal
 * @property {boolean} loading - Indicates if the delete operation is in progress
 * @property {() => Promise<any>} handleDelete - Async function to handle the delete operation
 * @property {string} [message] - Optional custom confirmation message
 * @property {string} [error] - Error message from the API (pass empty string or null when no error)
 * @property {boolean} [success] - Indicates if the delete operation was successful
 * @property {string} [successMessage] - Custom success message (default: "Item deleted successfully.")
 * @property {() => void} [onSuccessContinue] - Callback when "Continue" is clicked on success
 * @property {string} [customSuccessTitle] - Custom success title (default: "Deleted Successfully")
 * @property {string} [customErrorTitle] - Custom error title (default: "Delete Failed")
 * @property {string} [customConfirmTitle] - Custom confirm title (default: "Confirm Delete")
 * @property {string} [customActionButtonText] - Custom text for the action button (default: "Delete")
 * @property {string} [customLoadingButtonText] - Custom text for the action button while loading (default: "Deleting...")
 * @property {boolean} [isToggle] - Indicates if the delete operation is a toggle operation
 * @property {boolean} [isAcitve] - Indicates if the user is preiously enable or disable
 */

/**
 * A reusable delete confirmation modal component.
 *
 * @description
 * This component displays a confirmation modal with cancel and delete buttons.
 * It handles loading, success, and error states with appropriate UI feedback.
 */

const DeleteModal = ({
  showModal,
  handleClose,
  handleDelete,
  message = "Are you sure you want to delete this item? This action cannot be undone.",
  loading = false,
  error = "",
  success = false,
  successMessage = "Item deleted successfully.",
  onSuccessContinue,
  customSuccessTitle = "Deleted Successfully",
  customErrorTitle = "Delete Failed",
  customConfirmTitle = "Confirm Delete",
  customActionButtonText = "",
  customLoadingButtonText = "",
  isToggle = false,
  isActive = false,
}) => {
  const onDeleteClick = async () => {
    await handleDelete();
  };

  const handleSuccessContinue = () => {
    if (onSuccessContinue) {
      onSuccessContinue();
    } else {
      handleClose();
    }
  };

  // Determine icon and styling based on state
  const getIcon = () => {
    if (success) {
      return (
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10"
          style={{ width: "64px", height: "64px" }}
        >
          <i className="bi bi-check-circle-fill text-success fs-2"></i>
        </div>
      );
    }
    if (error) {
      return (
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-danger bg-opacity-10"
          style={{ width: "64px", height: "64px" }}
        >
          <i className="bi bi-x-circle-fill text-danger fs-2"></i>
        </div>
      );
    }
    return (
      <div
        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-danger bg-opacity-10"
        style={{ width: "64px", height: "64px" }}
      >
        <i className="bi bi-exclamation-triangle-fill text-danger fs-2"></i>
      </div>
    );
  };

  const getTitle = () => {
    if (success) return customSuccessTitle;
    if (error) return customErrorTitle;
    return customConfirmTitle;
  };

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      centered
      className="custom-modal"
      backdrop={loading ? "static" : true}
      keyboard={!loading}
    >
      <Modal.Body className="p-5">
        <div className="text-center mb-3">{getIcon()}</div>
        <div className="text-center mb-2">
          <h5 className="fw-bold fs-4">{getTitle()}</h5>
        </div>

        {/* Show success message when successful */}
        {success && (
          <p className="text-center text-success mb-4">{successMessage}</p>
        )}

        {/* Show confirmation message when not successful yet and no error */}
        {!success && !error && (
          <p className="text-center text-muted mb-4">{message}</p>
        )}

        {/* Show error message when there's an error */}
        {error && <p className="text-center text-danger mb-4">{error}</p>}

        {/* Success state - show Continue button */}
        {success && (
          <div className="d-flex justify-content-center">
            <Button
              className="custom-primary-button"
              onClick={handleSuccessContinue}
            >
              Continue
            </Button>
          </div>
        )}

        {/* Error state - show Cancel and Retry buttons */}
        {error && !success && (
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
            <Button
              className="custom-cancel-button"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              className="custom-delete-button"
              onClick={onDeleteClick}
              disabled={loading}
            >
              {loading ? "Retrying..." : "Retry"}
            </Button>
          </div>
        )}

        {/* Confirmation state - show Cancel and Delete buttons */}
        {!success && !error && (
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
            <Button
              className="custom-cancel-button"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              className="custom-delete-button"
              onClick={onDeleteClick}
              disabled={loading}
            >
              {loading
                ? customLoadingButtonText
                || (isToggle ? "Toggling..." : "Deleting...")
                : customActionButtonText
                || (isToggle
                  ? isActive
                    ? "Disable"
                    : "Enable"
                  : "Delete")}
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DeleteModal;
